import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { usePageTitle } from 'utils/pageTitle';
import {
	resolveInvitation,
	acceptInvitation,
} from 'data/repositories/invitationsRepository';
import { organizationsThunks } from 'data/repositories/organizationsRepository';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { INVITATION_TOKEN_PARAM } from 'view/helpers/subscription_helper/subscription_helper';

import Alert from 'components/Alert';
import Spinner from 'components/Spinner';

import './AuthPage.css';

/** HTTP statuses the accept classification branches on. */
const HTTP_UNAUTHORIZED = 401;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_CLIENT_ERROR_MIN = 400;
const HTTP_SERVER_ERROR_MIN = 500;

/**
 * How an accept rejection can be recovered from. The page renders the control
 * that matches the classification instead of collapsing every rejection into
 * one terminal message with no way forward.
 */
const ACCEPT_FAILURE = {
	SESSION_EXPIRED: 'SESSION_EXPIRED',
	BLOCKED: 'BLOCKED',
	TRANSIENT: 'TRANSIENT',
};

const RESOLVE_FALLBACK_MESSAGE =
	'We could not load this invitation right now. Please check your connection and try again in a moment.';
const ACCEPT_SESSION_EXPIRED_MESSAGE =
	'Your session has ended. Sign in again to accept this invitation.';
const ACCEPT_TRANSIENT_MESSAGE =
	'We could not reach the server to accept this invitation. Your invitation is still valid - please try again.';
const ACCEPT_BLOCKED_FALLBACK_MESSAGE =
	'This invitation could not be accepted.';

/** Where a successful join lands, and how long the success banner is held first. */
const DASHBOARD_PATH = '/dashboard';
const POST_ACCEPT_REDIRECT_MS = 1500;

/**
 * Key `apiService` reads to build the `X-Active-Organization` header, mirrored
 * across both web storages by `OrganizationProvider`.
 */
const ACTIVE_ORGANIZATION_STORAGE_KEY = 'active_organization_id';

/**
 * The two reads that together re-establish the client's organization context
 * after a join, and the one line every failure of either is reported under.
 */
const ORGANIZATION_REFRESH_THUNKS = [
	organizationsThunks.fetchAll,
	organizationsThunks.getActive,
];
const ORGANIZATION_REFRESH_FAILED_MESSAGE =
	'AcceptInvitation: organization refresh after accept failed';

/**
 * Drops the persisted active-organization id from both storages.
 *
 * Accepting an invitation makes the backend soft-delete the invitee's
 * provisional organization, so the id held here immediately names a row
 * `loadAndVerifyOrganization()` can no longer resolve. Left in place it keeps
 * riding on every request as `X-Active-Organization`, and the middleware's
 * header-present branch then leaves the request with no organization context
 * at all -- which is how a `PortfolioSet` written in that window lands with a
 * null `organization_id`. Storage access itself can throw (private browsing,
 * blocked site data), so each removal is guarded and reported rather than
 * allowed to abort the redirect.
 */
const clearPersistedActiveOrganization = () => {
	[localStorage, sessionStorage].forEach((storage) => {
		try {
			storage.removeItem(ACTIVE_ORGANIZATION_STORAGE_KEY);
		} catch (storageError) {
			console.error(
				'AcceptInvitation: could not clear the persisted active organization',
				{ key: ACTIVE_ORGANIZATION_STORAGE_KEY, error: storageError },
			);
		}
	});
};

/**
 * Session-storage key listing the invitation tokens this tab has accepted.
 * The accept page can still be reached for a consumed token through history
 * (the email-link entry that opened the round trip sits below everything the
 * page can replace), and the backend answers a consumed token with the same
 * uniform 404 as an invalid one. Remembering the tokens accepted here lets
 * the page send the new member on to the dashboard instead of telling them
 * their invitation is no longer valid.
 */
const ACCEPTED_INVITATIONS_STORAGE_KEY = 'accepted_invitation_tokens';

const readAcceptedInvitationTokens = () => {
	try {
		const stored = JSON.parse(
			sessionStorage.getItem(ACCEPTED_INVITATIONS_STORAGE_KEY) || '[]',
		);
		return Array.isArray(stored) ? stored : [];
	} catch (storageError) {
		console.error('AcceptInvitation: could not read accepted invitations', {
			key: ACCEPTED_INVITATIONS_STORAGE_KEY,
			error: storageError,
		});
		return [];
	}
};

const rememberAcceptedInvitation = (acceptedToken) => {
	try {
		const tokens = readAcceptedInvitationTokens();
		if (!tokens.includes(acceptedToken)) {
			sessionStorage.setItem(
				ACCEPTED_INVITATIONS_STORAGE_KEY,
				JSON.stringify([...tokens, acceptedToken]),
			);
		}
	} catch (storageError) {
		console.error('AcceptInvitation: could not remember accepted invitation', {
			key: ACCEPTED_INVITATIONS_STORAGE_KEY,
			error: storageError,
		});
	}
};

/**
 * True when a rejection carries a message the API itself wrote.
 *
 * `apiService.get()` throws the parsed response envelope for an
 * application-level failure, so a backend message arrives as a property of a
 * plain object. A failure below the application layer -- a transport
 * TypeError, or a non-JSON gateway body whose parse throws -- rejects with the
 * Error itself, whose message is a fetch or parser string that must never be
 * shown to a visitor as their invitation status.
 */
const hasApiSuppliedMessage = (error) =>
	!!error &&
	typeof error === 'object' &&
	!(error instanceof Error) &&
	typeof error.message === 'string' &&
	error.message.trim() !== '';

/**
 * Classifies an accept rejection into the recovery it needs.
 *
 * `apiService.post()` attaches `status` whenever the API answered with a JSON
 * envelope, so a rejection carrying no status came from transport or from a
 * body that could not be parsed and is always retryable. A 401 is a session
 * problem, recovered through sign-in and never through sign-up. Any other
 * deliberate 4xx is the backend refusing this invitation and is terminal,
 * while a timeout, a rate limit and every 5xx leave the invitation untouched
 * on the server and so stay retryable.
 */
const classifyAcceptFailure = (error) => {
	const status = typeof error?.status === 'number' ? error.status : null;

	if (status === HTTP_UNAUTHORIZED) {
		return ACCEPT_FAILURE.SESSION_EXPIRED;
	}

	if (
		status !== null &&
		status >= HTTP_CLIENT_ERROR_MIN &&
		status < HTTP_SERVER_ERROR_MIN &&
		status !== HTTP_REQUEST_TIMEOUT &&
		status !== HTTP_TOO_MANY_REQUESTS
	) {
		return ACCEPT_FAILURE.BLOCKED;
	}

	return ACCEPT_FAILURE.TRANSIENT;
};

/**
 * Picks the copy for a classified accept rejection. Only BLOCKED shows the
 * backend reason verbatim, because only there did the backend deliberately
 * explain why accepting cannot proceed.
 */
const acceptFailureMessage = (kind, error) => {
	if (kind === ACCEPT_FAILURE.SESSION_EXPIRED) {
		return ACCEPT_SESSION_EXPIRED_MESSAGE;
	}

	if (kind === ACCEPT_FAILURE.BLOCKED) {
		return typeof error?.message === 'string' && error.message.trim() !== ''
			? error.message
			: ACCEPT_BLOCKED_FALLBACK_MESSAGE;
	}

	return ACCEPT_TRANSIENT_MESSAGE;
};

/** Label for the accept control, which doubles as the retry after a fault. */
const acceptButtonLabel = (isAccepting, canRetry) => {
	if (isAccepting) {
		return 'Accepting...';
	}

	return canRetry ? 'Try again' : 'Accept Invitation';
};

/**
 * AcceptInvitation
 *
 * Public, token-in-URL landing page for an organization invitation email.
 * Reached by someone who may never have signed in, so it must never assume
 * `state.user` is populated and must never redirect to sign-in on mount --
 * see `resolveInvitation` in `invitationsRepository.js`, which deliberately
 * sends no authToken because `GET /api/invitations/{token}` is public.
 *
 * `POST /api/invitations/{token}/accept`, in contrast, sits inside the
 * jwt.auth middleware group and 401s for an anonymous caller, so the Accept
 * action is only ever offered to a signed-in visitor. A visitor with no
 * session is offered both ways back to one -- sign-up and sign-in -- each
 * carrying the token, so an invitee who already has an account is not pushed
 * into a sign-up that fails validation on a duplicate email.
 */
const AcceptInvitation = (props) => {
	usePageTitle('Accept Invitation');
	const dispatch = useDispatch();
	const history = useHistory();
	let { token } = useParams();

	const isLoggedIn = useSelector((state) => !!state.user.authToken);

	const [isResolving, setIsResolving] = useState(true);
	const [invitation, setInvitation] = useState(null);
	const [resolveError, setResolveError] = useState('');

	const [isAccepting, setIsAccepting] = useState(false);
	const [acceptFailure, setAcceptFailure] = useState(null);
	const [joined, setJoined] = useState(false);

	// Id of the pending post-accept redirect, cleared if the page goes away
	// first so a scheduled navigation cannot land after unmount.
	const redirectTimerRef = useRef(null);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		// Already accepted in this tab: the token is consumed, so resolving it
		// can only produce the dead-invitation 404. Go on to the dashboard.
		if (readAcceptedInvitationTokens().includes(token)) {
			history.replace(DASHBOARD_PATH);
			return;
		}

		setIsResolving(true);
		setResolveError('');

		dispatch(resolveInvitation(token))
			.then((result) => {
				setInvitation(result);
			})
			.catch((error) => {
				// The backend returns a uniform 404 for an invalid, expired, or
				// already-used token -- do not try to distinguish those cases here,
				// and show its message verbatim. A failure below the application
				// layer carries no backend message and must not be reported as a
				// dead invitation: the token can be perfectly valid while the fault
				// is purely infrastructural.
				setResolveError(
					hasApiSuppliedMessage(error)
						? error.message
						: RESOLVE_FALLBACK_MESSAGE,
				);
			})
			.finally(() => {
				setIsResolving(false);
			});
	}, [token]);

	useEffect(
		() => () => {
			if (redirectTimerRef.current !== null) {
				clearTimeout(redirectTimerRef.current);
				redirectTimerRef.current = null;
			}
		},
		[],
	);

	/**
	 * Brings the client's organization surface back in line with the join that
	 * just happened: the stale provisional id stops being persisted, then the
	 * organization list and the active organization are re-read from the API.
	 *
	 * The join has already succeeded on the server by the time this runs, so
	 * neither refetch may gate the redirect or reach the invitee as an error.
	 * Each is therefore isolated from the other and from its caller against
	 * both a rejection and a synchronous throw -- a transport fault raises the
	 * latter out of `dispatch` itself -- and every failure is logged with its
	 * context rather than swallowed.
	 */
	const refreshOrganizationSurface = useCallback(() => {
		clearPersistedActiveOrganization();

		ORGANIZATION_REFRESH_THUNKS.forEach((organizationThunk) => {
			try {
				Promise.resolve(dispatch(organizationThunk())).catch(
					(refreshError) => {
						console.error(ORGANIZATION_REFRESH_FAILED_MESSAGE, refreshError);
					},
				);
			} catch (refreshError) {
				console.error(ORGANIZATION_REFRESH_FAILED_MESSAGE, refreshError);
			}
		});
	}, [dispatch]);

	const goToSignUp = useCallback(() => {
		history.push(
			`/sign-up?${INVITATION_TOKEN_PARAM}=${encodeURIComponent(token)}`,
		);
	}, [history, token]);

	/**
	 * Routes to sign-in carrying the token, so `getPostAuthRedirect` returns
	 * the authenticated visitor to this invitation instead of the dashboard.
	 */
	const goToSignIn = useCallback(() => {
		history.push(
			`/sign-in?${INVITATION_TOKEN_PARAM}=${encodeURIComponent(token)}`,
		);
	}, [history, token]);

	const onAccept = useCallback(
		(confirmed = false) => {
			setIsAccepting(true);
			setAcceptFailure(null);

			dispatch(acceptInvitation(token, { confirmed }))
				.then((result) => {
					if (result?.requiresConfirmation) {
						dispatch(
							openModal(MODAL_TYPES.ACCEPT_INVITATION, {
								organizationName: result.organizationName,
								propertyCount: result.propertyCount,
								onConfirm: () => onAccept(true),
							}),
						);
						return;
					}

					setJoined(true);
					rememberAcceptedInvitation(token);
					refreshOrganizationSurface();

					// `replace`, not `push`: the token is consumed now, so leaving
					// this URL in history means Back re-resolves it into the uniform
					// 404 and tells someone who just joined that their invitation is
					// no longer valid.
					redirectTimerRef.current = setTimeout(
						() => history.replace(DASHBOARD_PATH),
						POST_ACCEPT_REDIRECT_MS,
					);
				})
				.catch((error) => {
					const kind = classifyAcceptFailure(error);

					setAcceptFailure({
						kind,
						message: acceptFailureMessage(kind, error),
					});
				})
				.finally(() => {
					setIsAccepting(false);
				});
		},
		[dispatch, token, history, refreshOrganizationSurface],
	);

	const sessionExpired = acceptFailure?.kind === ACCEPT_FAILURE.SESSION_EXPIRED;
	const blocked = acceptFailure?.kind === ACCEPT_FAILURE.BLOCKED;
	const canRetryAccept = acceptFailure?.kind === ACCEPT_FAILURE.TRANSIENT;

	return (
		<div className="auth-page">
			<div className="auth-page-content">
				<h1>Accept Invitation</h1>

				<div className="sign-in-wrapper">
					{isResolving && <Spinner />}

					{!isResolving && resolveError && (
						<Alert color="danger" text={resolveError} />
					)}

					{!isResolving && !resolveError && invitation && (
						<>
							<p>
								<strong>{invitation.invitedByName}</strong> invited you to
								join <strong>{invitation.organizationName}</strong> as a{' '}
								<strong>{invitation.role}</strong>.
							</p>

							{joined && (
								<Alert
									color="success"
									text="You have joined the organization. Redirecting..."
								/>
							)}

							{!joined && acceptFailure && (
								<Alert color="danger" text={acceptFailure.message} />
							)}

							{/* An expired session is a sign-in problem: this visitor
							    demonstrably has an account, so sign-up is never
							    offered here. */}
							{!joined && sessionExpired && (
								<div className="input-wrapper">
									<button
										onClick={goToSignIn}
										className="btn btn-primary"
									>
										Sign in to accept
									</button>
								</div>
							)}

							{!joined && !sessionExpired && !isLoggedIn && (
								<div className="input-wrapper">
									<button
										onClick={goToSignUp}
										className="btn btn-primary"
									>
										Create an account to accept
									</button>
									<button
										onClick={goToSignIn}
										className="btn btn-secondary"
									>
										Sign in to accept
									</button>
								</div>
							)}

							{/* BLOCKED is the only terminal case -- the backend's reason
							    explains why accepting cannot proceed. Every other
							    rejection leaves the invitation valid on the server, so
							    the control stays and re-issues the accept. */}
							{!joined && !sessionExpired && isLoggedIn && !blocked && (
								<div className="input-wrapper">
									<button
										onClick={() => onAccept(false)}
										disabled={isAccepting}
										className="btn btn-primary"
									>
										{acceptButtonLabel(isAccepting, canRetryAccept)}
									</button>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default AcceptInvitation;
